/**
 * CapabilitiesGuide.jsx — 🟪 P24.3
 *
 * Guía de Capacidades v1.0 (owner-approved). Reemplaza al highlight
 * simple del recuadro azul de la Sección 1 "Bienvenido a DamyxIA" en el
 * HomepageModal. Diseño de tarjetas con scroll interno para los CTAs.
 *
 * Bilingüe (ES / EN) mediante la prop `t`, que debe recibir el objeto
 * `guide` del diccionario correspondiente.
 */
import React, { useRef } from 'react';
import {
  MessageCircle, Unlock, Lock, Globe, Smartphone, FileText,
  Settings, AlertTriangle, CheckCircle2, XCircle, ArrowRight,
} from 'lucide-react';

export default function CapabilitiesGuide({ t }) {
  const modesRef = useRef(null);
  const formulasRef = useRef(null);

  const scrollTo = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      className="rounded-xl border border-indigo-500/30 bg-slate-950/50 p-4 sm:p-6 space-y-8"
      data-testid="capabilities-guide"
    >
      {/* Header */}
      <div className="text-center space-y-2">
        <span
          className="inline-block px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-semibold border border-indigo-500/30"
          data-testid="guide-badge"
        >
          {t.badge}
        </span>
        <h3 className="text-xl sm:text-2xl font-bold text-white">
          {t.title}
        </h3>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          {t.subtitle}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
          <button
            onClick={() => scrollTo(modesRef)}
            className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors"
            data-testid="guide-cta-modes"
          >
            {t.ctaModes}
          </button>
          <button
            onClick={() => scrollTo(formulasRef)}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium border border-slate-700 transition-colors"
            data-testid="guide-cta-formulas"
          >
            {t.ctaFormulas}
          </button>
        </div>
      </div>

      {/* Bloque 1 · Los 2 modos de trabajo */}
      <section ref={modesRef} className="space-y-4 scroll-mt-4">
        <div className="text-center">
          <h4 className="text-lg font-semibold text-white">{t.modesTitle}</h4>
          <p className="text-xs text-slate-400 mt-1">{t.modesSubtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ModeCard
            icon={MessageCircle}
            iconColor="text-sky-400"
            title={t.modeChatTitle}
            tag={t.modeChatTag}
            tagColor="bg-sky-500/15 text-sky-300 border-sky-500/30"
            desc={t.modeChatDesc}
            testid="guide-mode-chat"
          />
          <ModeCard
            icon={Unlock}
            iconColor="text-emerald-400"
            title={t.modeBuilderTitle}
            tag={t.modeBuilderTag}
            tagColor="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
            desc={t.modeBuilderDesc}
            testid="guide-mode-builder"
          />
        </div>
      </section>

      {/* Bloque 2 · Modo Chat — Qué funciona en el Preview */}
      <section className="space-y-4">
        <div className="flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-sky-400 shrink-0 mt-1" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-lg font-semibold text-white">{t.chatCapsTitle}</h4>
              <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
                {t.chatCapsTag}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">{t.chatCapsSubtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ✅ Sí funciona */}
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2"
            data-testid="guide-chat-works"
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <h5 className="text-sm font-semibold text-emerald-300">
                {t.chatWorksTitle}
              </h5>
            </div>
            <ul className="space-y-1.5">
              {t.chatWorks.map(([name, desc], i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-emerald-400 mt-0.5">✅</span>
                  <div>
                    <div className="text-slate-200 font-medium">{name}</div>
                    <div className="text-slate-400">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* ❌ No funciona */}
          <div
            className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 space-y-2"
            data-testid="guide-chat-fails"
          >
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-rose-400" />
              <h5 className="text-sm font-semibold text-rose-300">
                {t.chatFailsTitle}
              </h5>
            </div>
            <ul className="space-y-1.5">
              {t.chatFails.map(([name, desc, error], i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-rose-400 mt-0.5">❌</span>
                  <div>
                    <div className="text-slate-200 font-medium">{name}</div>
                    <div className="text-slate-400">{desc}</div>
                    <div className="text-rose-400/80 font-mono text-[10px] mt-0.5">
                      {error}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Bloque 3 · Modo Builder — Todo lo que puedes descargar */}
      <section className="space-y-4">
        <div className="flex items-start gap-3">
          <Unlock className="w-5 h-5 text-emerald-400 shrink-0 mt-1" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-lg font-semibold text-white">{t.builderTitle}</h4>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                {t.builderTag}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">{t.builderSubtitle}</p>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">{t.builderWarning}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BuilderList
            icon={Globe}
            iconColor="text-sky-400"
            title={t.builderWebTitle}
            items={t.builderWeb}
            testid="guide-builder-web"
          />
          <BuilderList
            icon={Smartphone}
            iconColor="text-fuchsia-400"
            title={t.builderMobileTitle}
            items={t.builderMobile}
            testid="guide-builder-mobile"
          />
        </div>
      </section>

      {/* Bloque 4 · Las fórmulas que siempre funcionan */}
      <section ref={formulasRef} className="space-y-4 scroll-mt-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-indigo-400 shrink-0 mt-1" />
          <div>
            <h4 className="text-lg font-semibold text-white">{t.formulasTitle}</h4>
            <p className="text-xs text-slate-400 mt-1">{t.formulasSubtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormulaCard
            icon={MessageCircle}
            iconColor="text-sky-400"
            title={t.formulaChatTitle}
            code={t.formulaChatCode}
            testid="guide-formula-chat"
          />
          <FormulaCard
            icon={Unlock}
            iconColor="text-emerald-400"
            title={t.formulaBuilderWebTitle}
            code={t.formulaBuilderWebCode}
            testid="guide-formula-builder-web"
          />
          <FormulaCard
            icon={Smartphone}
            iconColor="text-fuchsia-400"
            title={t.formulaBuilderMobileTitle}
            code={t.formulaBuilderMobileCode}
            testid="guide-formula-builder-mobile"
          />
        </div>
      </section>

      {/* Bloque 5 · Cómo activar el Builder */}
      <section className="space-y-4">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-slate-300 shrink-0 mt-1" />
          <div>
            <h4 className="text-lg font-semibold text-white">{t.stepsTitle}</h4>
            <p className="text-xs text-slate-400 mt-1">{t.stepsSubtitle}</p>
          </div>
        </div>

        <ol className="space-y-2" data-testid="guide-steps">
          {t.steps.map(([title, desc], i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-lg border border-slate-700/60 bg-slate-900/40 p-3"
            >
              <span
                className="shrink-0 w-7 h-7 rounded-full bg-indigo-500 text-white text-sm font-bold flex items-center justify-center"
                aria-hidden
              >
                {i + 1}
              </span>
              <div>
                <div className="text-sm font-semibold text-slate-100">{title}</div>
                <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
              </div>
            </li>
          ))}
        </ol>

        {/* Demo visual del toggle */}
        <div className="flex items-center justify-center gap-3 pt-2">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 text-slate-400 text-xs font-medium">
            <Lock className="w-3.5 h-3.5" /> {t.toggleOff}
          </span>
          <ArrowRight className="w-4 h-4 text-slate-500" />
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-medium">
            <Unlock className="w-3.5 h-3.5" /> {t.toggleOn}
          </span>
        </div>
      </section>
    </div>
  );
}

// ---------- Sub-componentes ----------
function ModeCard({ icon: Icon, iconColor, title, tag, tagColor, desc, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 space-y-2"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-6 h-6 ${iconColor} shrink-0`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h5 className="text-sm font-semibold text-white">{title}</h5>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tagColor}`}>
              {tag}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function BuilderList({ icon: Icon, iconColor, title, items, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 space-y-2"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h5 className="text-sm font-semibold text-slate-100">{title}</h5>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="text-emerald-400 mt-0.5">✅</span>
            <span className="text-slate-300">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FormulaCard({ icon: Icon, iconColor, title, code, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 space-y-2 flex flex-col"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h5 className="text-sm font-semibold text-slate-100">{title}</h5>
      </div>
      <pre className="text-[11px] leading-relaxed font-mono text-slate-300 bg-slate-950/60 border border-slate-800 rounded p-2 whitespace-pre-wrap flex-1">
        {code}
      </pre>
    </div>
  );
}
